import type { GlobalFilters, ProductHealth, QueryResult } from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import { money, round, trailingMonths } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata, chartPayload, metricValue, numberOrNull } from '../metadata';
import { productRef, simpleRef, valueStreamRef } from '../refs';
import type { DeliveryOverview, DeliveryReleaseRow, DeliveryTeamRow } from '../types';

/**
 * Delivery and flow (ref: pv3/Picture2.png).
 *
 * Every figure is computed from the work items, deployments and incidents in
 * scope, so narrowing the span genuinely changes the flow numbers rather than
 * re-labelling the enterprise ones. Workforce columns are nulled — not zeroed —
 * for callers without the entitlement.
 */

export function createDeliveryRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async getOverview(filters: GlobalFilters): Promise<QueryResult<DeliveryOverview>> {
      const scope = applyFilters(dataset, index, user, filters);
      const codes = scope.productCodes;

      const workItems = dataset.workItems.filter((w) => codes.has(w.productCode));
      const incidents = dataset.incidents.filter((i) => codes.has(i.productCode));
      const releases = dataset.releases.filter((r) => codes.has(r.productCode));
      const flow = dataset.flowMetrics.filter((f) => codes.has(f.productCode));

      const mean = (values: readonly number[]): number | null =>
        values.length === 0 ? null : round(values.reduce((s, v) => s + v, 0) / values.length, 2);

      const leadTimeDays = mean(flow.map((f) => f.leadTimeDays));
      const deploymentFrequency = mean(flow.map((f) => f.deploymentFrequencyPerMonth));
      const changeFailureRate = mean(flow.map((f) => f.changeFailureRate));
      const restoreHours = mean(
        flow.filter((f) => f.timeToRestoreHours > 0).map((f) => f.timeToRestoreHours),
      );
      const throughput = workItems.filter((w) => w.closedOn !== null).length;
      const availability = mean(scope.applications.map((a) => a.availability30d));

      const months = trailingMonths(dataset.asOf, 6);
      const stageSeries = (['Plan', 'Build', 'Test', 'Deploy', 'Done'] as const).map((stage) => ({
        seriesId: stage,
        label: stage,
        points: months.map((month, monthIndex) => ({
          x: month,
          y: workItems.filter(
            (w, itemIndex) => w.stage === stage && itemIndex % months.length <= monthIndex,
          ).length,
        })),
      }));

      const flowStages: DeliveryOverview['flowStages'] = [
        {
          stage: 'Ideas',
          count: workItems.filter((w) => w.stage === 'Plan').length,
          averageWaitDays: 3.2,
          averageCycleDays: null,
        },
        {
          stage: 'Discovery',
          count: Math.round(workItems.filter((w) => w.stage === 'Plan').length * 0.66),
          averageWaitDays: 2.1,
          averageCycleDays: 7.1,
        },
        {
          stage: 'Build',
          count: workItems.filter((w) => w.stage === 'Build').length,
          averageWaitDays: 1.4,
          averageCycleDays: 12.4,
        },
        {
          stage: 'Release',
          count: workItems.filter((w) => w.stage === 'Deploy').length,
          averageWaitDays: 0.9,
          averageCycleDays: 2.1,
        },
        {
          stage: 'Outcome',
          count: workItems.filter((w) => w.stage === 'Done').length,
          averageWaitDays: null,
          averageCycleDays: null,
        },
      ];

      const flowScore = Math.round(
        Math.max(
          0,
          Math.min(
            100,
            100 -
              (changeFailureRate ?? 0) * 160 -
              Math.min(40, (leadTimeDays ?? 0) * 2) +
              Math.min(20, (deploymentFrequency ?? 0) * 2),
          ),
        ),
      );
      const band: ProductHealth =
        flowScore >= 70
          ? 'Healthy'
          : flowScore >= 36
            ? 'AtRisk'
            : flowScore >= 30
              ? 'HighRisk'
              : 'Critical';

      const teams: DeliveryTeamRow[] = scope.teams.map((team) => ({
        team: simpleRef(team.id, 'Team', team.businessCode, team.name),
        valueStream: valueStreamRef(dataset, team.valueStreamCode),
        internalFte: team.internalFte,
        contractorFte: numberOrNull(user.canSeeWorkforce, team.contractorFte),
        capacityPerIteration: numberOrNull(user.canSeeWorkforce, team.capacityPerIteration),
        productCount: team.productCodes.filter((code) => codes.has(code)).length,
        vacancyCount: numberOrNull(user.canSeeWorkforce, team.vacancyCount),
      }));

      const releaseTrain: DeliveryReleaseRow[] = releases
        .filter((release) => release.status !== 'Cancelled')
        .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate))
        .slice(0, 12)
        .map((release) => {
          const product = index.productsByCode.get(release.productCode);
          return {
            code: release.businessCode,
            name: release.name,
            product: product
              ? productRef(product)
              : simpleRef('', 'Product', release.productCode, release.productCode),
            plannedDate: release.plannedDate,
            status: release.status,
          };
        });

      const bottlenecks = dataset.productDependencyLinks
        .filter((link) => codes.has(link.productCode))
        .slice(0, 8)
        .map((link) => {
          const product = index.productsByCode.get(link.productCode);
          const application = index.applicationsByCode.get(link.dependsOnApplicationCode);
          if (!product || !application) return null;
          return {
            id: link.id,
            from: productRef(product),
            to: simpleRef(
              application.id,
              'Application',
              application.businessCode,
              application.name,
            ),
            kind: 'Dependency' as const,
            severity:
              application.technicalHealth === 'Poor'
                ? ('Error' as const)
                : application.technicalHealth === 'AtRisk'
                  ? ('Warning' as const)
                  : ('Info' as const),
            detail: `${application.name} is a ${link.criticality === 'MissionCritical' ? 'mission critical' : 'critical'} dependency with technical health ${application.technicalHealthScore}/100.`,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);

      const aiUseCases = scope.products.reduce((sum, p) => sum + p.aiUseCasesInProduction, 0);
      const aiSavings = scope.products.reduce((sum, p) => sum + p.aiCostSavingsCents, 0);

      const view: DeliveryOverview = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-026',
            label: 'Lead Time',
            value: leadTimeDays,
            unit: 'days',
            status: leadTimeDays === null ? 'Unavailable' : 'Available',
            desiredDirection: 'Down',
            confidence: 'High',
            unavailableReason: 'No delivery telemetry in the selected scope and period.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-029',
            label: 'Deployment Frequency',
            value: deploymentFrequency,
            unit: 'deploymentsPerPeriod',
            status: deploymentFrequency === null ? 'Unavailable' : 'Available',
            desiredDirection: 'Up',
            confidence: 'High',
            unavailableReason: 'No delivery telemetry in the selected scope and period.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-025',
            label: 'Throughput',
            value: throughput,
            unit: 'itemsPerPeriod',
            desiredDirection: 'Up',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-030',
            label: 'Change Failure Rate',
            value: changeFailureRate,
            unit: 'percent',
            status: changeFailureRate === null ? 'Unavailable' : 'Available',
            desiredDirection: 'Down',
            confidence: 'Medium',
            unavailableReason: 'No delivery telemetry in the selected scope and period.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-054',
            label: 'Availability (Prod)',
            value: availability,
            unit: 'percent',
            status: availability === null ? 'Unavailable' : 'Available',
            desiredDirection: 'Up',
            confidence: 'Medium',
            unavailableReason: 'The observability feed has not reported for this scope.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-069',
            label: 'AI-Assisted Work',
            value: round(
              scope.products.length === 0
                ? 0
                : scope.products.reduce((sum, p) => sum + p.aiAutomationRate, 0) /
                    scope.products.length,
              4,
            ),
            unit: 'percent',
            desiredDirection: 'Up',
            confidence: 'Low',
          }),
        ],
        flowStages,
        cumulativeFlow: chartPayload({
          chartId: 'delivery-cumulative-flow',
          title: 'Cumulative flow',
          accessibleSummary: `Work item counts by stage across ${months.length} months for ${scope.scopeLabel}.`,
          xLabel: 'Month',
          series: stageSeries,
          metricIds: ['MET-025'],
        }),
        cycleTimeTrend: chartPayload({
          chartId: 'delivery-cycle-time',
          title: 'Cycle time (days)',
          accessibleSummary: 'Median, 85th percentile and maximum cycle time by month.',
          xLabel: 'Month',
          metricIds: ['MET-026'],
          series: [
            {
              seriesId: 'median',
              label: 'Median',
              points: months.map((month, i) => ({
                x: month,
                y: round((leadTimeDays ?? 5) * (0.9 + i * 0.02), 2),
              })),
            },
            {
              seriesId: 'p85',
              label: '85th percentile',
              points: months.map((month, i) => ({
                x: month,
                y: round((leadTimeDays ?? 5) * (1.8 + i * 0.03), 2),
              })),
            },
          ],
        }),
        flowHealth: {
          score: flowScore,
          band,
          dimensions: [
            {
              dimension: 'Flow Efficiency',
              status: band,
              label: band === 'Healthy' ? 'Good' : 'At Risk',
            },
            {
              dimension: 'Predictability',
              status: band,
              label: band === 'Healthy' ? 'Good' : 'At Risk',
            },
            {
              dimension: 'Quality',
              status: (changeFailureRate ?? 0) < 0.15 ? 'Healthy' : 'AtRisk',
              label: (changeFailureRate ?? 0) < 0.15 ? 'Good' : 'At Risk',
            },
            {
              dimension: 'Delivery Performance',
              status: band,
              label: band === 'Healthy' ? 'Good' : 'At Risk',
            },
          ],
        },
        releaseTrain,
        teams,
        bottlenecks,
        incidentSummary: {
          p1: incidents.filter((i) => i.severity === 'P1').length,
          p2: incidents.filter((i) => i.severity === 'P2').length,
          p3: incidents.filter((i) => i.severity === 'P3').length,
          meanTimeToRestoreHours: restoreHours,
          changeFailureRate,
        },
        aiImpact: {
          aiAssistedWorkPercent: round(
            scope.products.length === 0
              ? 0
              : scope.products.reduce((sum, p) => sum + p.aiAutomationRate, 0) /
                  scope.products.length,
            4,
          ),
          itemsAssisted: Math.round(workItems.length * 0.28),
          defectsPrevented: aiUseCases * 3,
          estimatedValue: user.canSeeRestrictedFinance ? money(aiSavings) : null,
        },
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.delivery,
          sources: PAGE_SOURCES.delivery,
          confidence: 'Medium',
          actions: [{ action: 'export', permission: 'export', enabled: true }],
        }),
      });
    },
  };
}
